import React, { useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Path, useFormContext, UseFormReturn, useWatch } from 'react-hook-form';
import { DictionaryMdService } from '@spilca/dictionary_ts/v1/dictionary_web/dictionaries_md_pb_service';
import {
  ListProductRequest,
  ListProductResponse,
  ProductFilter
} from '@spilca/dictionary_ts/v1/dictionary_md/product_pb';
import { int64 } from '@monorepo/helpers';
import { Product } from '@spilca/order_ts/v1/order/model_product_pb';
import { LookUpField, TFormField } from '@monorepo/forms';
import { getGrpcPromise, TGRPCPromiseReject } from '@monorepo/grpc';

type TProductField<TFormValues> = {
  valueKey: Path<TFormValues>;
  product: Product.AsObject;
} & TFormField<TFormValues>;

const PackagesField = <
  TFormValues extends Record<string, any> = Record<string, any>
>({
  name,
  pricing,
  valueKey,
  ...props
}: TProductField<TFormValues>) => {
  const { setValue }: UseFormReturn<TFormValues> =
    useFormContext<TFormValues>();

  const product = useWatch({ name: 'product' });

  useEffect(() => {
    if (pricing.product.id && pricing.product.id !== product.id) {
      setValue(name, '');
      setValue('unitPrice', '');
    }
  }, [product]);

  const handleSelect = (data: any) => {
    setValue(valueKey, data);
  };

  const searchMutator = (): Promise<ListProductResponse.AsObject> => {
    const request = new ListProductRequest();
    const filter = new ProductFilter();
    const { type, categoryId, process, id } = product;
    filter.setType(int64(type));
    filter.setProcess(int64(process));
    filter.setCategoryId(int64(categoryId));
    filter.setId(int64(id));
    request.setFilter(filter);

    return getGrpcPromise(DictionaryMdService.ListProduct)(request);
  };

  const { mutate, isLoading, data } = useMutation<
    ListProductResponse.AsObject,
    TGRPCPromiseReject,
    string
  >({ mutationFn: searchMutator, retry: false });

  const items = useMemo(
    () =>
      data?.itemsList?.length
        ? data.itemsList[0]?.packagesList.map((item) => ({
            ...item,
            name: `${item.amount / 1000 || 0} / ${item.quantityTypeShortName}`
          }))
        : [],
    [data]
  );

  return (
    <LookUpField
      name={name}
      searchUp
      searchFc={mutate}
      isLoading={isLoading}
      items={items}
      onSelect={handleSelect}
      {...props}
    />
  );
};

export default PackagesField;
